/**
 * Guard enforcing a company's Require MFA setting.
 *
 * A Ninety session outlives a Cognito one, so a long-lived user reaches this guard with no readable
 * MFA preference. That state used to be indistinguishable from a Microsoft SSO user — both make the
 * Amplify calls throw — and everyone in it was waved past the company setting.
 *
 * `authProvider` tells them apart. It is a claim on this session's own tokens, so it answers for the
 * browser asking; the person record holds only the most recent login on any device, and would let an
 * SSO sign-in elsewhere exempt a Cognito-native session here.
 *
 * Logout rather than `return false`: setup() needs the same Cognito session that is missing, and
 * redirecting to /login while the Ninety token is still valid bounces off IsAnonymousGuard to /home.
 *
 * @returns Observable<boolean> indicating whether navigation is allowed.
 */
import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { Action, Store } from '@ngrx/store';
import { catchError, combineLatest, filter, map, of, tap } from 'rxjs';

import { AuthStateSelectors } from '@ninety/platform/state/app-global/auth/auth.selectors';
import { LoginMfaActions } from '@ninety/platform/state/app-global/auth/login/login.actions';
import { LogOutActions } from '@ninety/platform/state/app-global/auth/logout/logout.actions';
import { AmplifyService } from '@ninety/platform/state/app-global/auth/services/amplify.service';
import { selectCompany } from '@ninety/platform/state/app-global/company/company-state.selectors';

/** Distinguishes "Cognito says no factors" from "Cognito could not be reached at all". */
const COGNITO_UNAVAILABLE = Symbol('cognito-unavailable');

type MfaDecision = { allowed: boolean; action?: Action };

export const companyMfaSetupGuard: CanActivateFn = () => {
  const store = inject(Store);
  const amplifyService = inject(AmplifyService);

  return combineLatest([
    store.select(selectCompany),
    store.select(AuthStateSelectors.authProvider),
    amplifyService.fetchMfaPreference().pipe(catchError(() => of(COGNITO_UNAVAILABLE))),
    amplifyService.getUserAttributes().pipe(catchError(() => of(COGNITO_UNAVAILABLE))),
  ]).pipe(
    // Company arrives late on a company switch. Waiting on it also settles authProvider, which the
    // reducer sets from the same action before the effect that dispatches the company.
    filter(([company]) => !!company),
    map(([company, authProvider, mfaPreference, cognitoAttributes]): MfaDecision => {
      if (!company.settings.requireMfa) return { allowed: true };

      // Both calls are the same GetUser, so only both failing means the session is really gone --
      // and only then is there no live answer to prefer over the claim.
      if (mfaPreference === COGNITO_UNAVAILABLE && cognitoAttributes === COGNITO_UNAVAILABLE) {
        if (authProvider === 'Google' || authProvider === 'Microsoft') return { allowed: true };

        // Cognito-native, or a session predating the claim: unverifiable either way.
        return { allowed: false, action: LogOutActions.initiateLogOut() };
      }

      // identities is present only on a Cognito-federated (Google) login.
      const userIsSsoCognito = cognitoAttributes !== COGNITO_UNAVAILABLE && !!cognitoAttributes['identities'];
      if (userIsSsoCognito) return { allowed: true };

      if (mfaPreference !== COGNITO_UNAVAILABLE && mfaPreference.enabled?.length > 0) return { allowed: true };

      // One call failed but the other proves the session lives, so setup can still run.
      return { allowed: false, action: LoginMfaActions.setup() };
    }),
    tap(({ action }) => {
      if (action) store.dispatch(action);
    }),
    map(({ allowed }) => allowed)
  );
};
